import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  Box
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  CheckmarkCircle20Px,
  CloseCircle20Px,
  Warning20Px,
  useToast
} from "@qubit/autoparts";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import { ActivityState } from "~/features/autostoreBin/BinState";
import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import {
  getBinCompartmentOccupancy,
  isBinResponseCompartmentEmpty
} from "~/lib/binCompartmentOccupancy";
import { getBarcodeValue } from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import {
  selectIsShortSideBin,
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import { usePostBinNotEmptyInventoryMovementMutation } from "~/redux/warehouse/autostoreGrid.hooks";

import { GetBinResponse } from "~/types/api";

import { setSourceDestinationBins } from "./binMaintenanceWorkstation.slice";

type BinNotEmptyButtonProps = {
  state: ActivityState;
  binId: number | undefined;
  numberOfRows: number;
  numberOfColumns: number;
  portId: number;
  binState: GetBinResponse | undefined;
};

export function BinNotEmptyButton(props: BinNotEmptyButtonProps) {
  const { state, binId, numberOfRows, numberOfColumns, binState } = props;
  const theme = useTheme();
  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName("Bin Not Empty");
  const dispatch = useAppDispatch();
  const { errorToast, successToast } = useToast();
  const { t } = useTranslation();
  const gridId = useAppSelector(selectWorkstationAutostoreGridId);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const isSourceBinReady = useAppSelector(
    (state) => state.binMaintenanceWorkstation.isSourceBinReady
  );

  const [barcodeInput, setBarcodeInput] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedCompartment, setSelectedCompartment] = useState<number | null>(
    null
  );

  const selectedCompartmentBinId =
    selectedCompartment !== null &&
    binState?.compartments[selectedCompartment].binId;

  const [setBinNotEmpty, { isLoading: isSetBinNotEmptyLoading }] =
    usePostBinNotEmptyInventoryMovementMutation();

  useKeyDownHandler();
  useBarcodeScanner({
    findScanMatch: (initialBuffer: string) => {
      const barcodeValue: string = getBarcodeValue(initialBuffer);
      setBarcodeInput(barcodeValue);
      mixpanelTrack({
        type: "Scan",
        label: "Scan UPC",
        trackedPageName: trackedModalName,
        eventProperties: {
          UPC: barcodeValue
        }
      });
    }
  });

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!isModalOpen) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setBarcodeInput("");
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setSelectedCompartment(null);
    }
  }, [isModalOpen]);

  // auto select compartment for whole bins
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!isModalOpen) return;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (binState?.compartments.length === 1) {
      const isCompartmentEmpty = isBinResponseCompartmentEmpty(binState, 0);
      if (isCompartmentEmpty) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setSelectedCompartment(0);
      }
    }
    // intentionally leaving out binState to prevent this from firing every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binState?.binState.binId, isModalOpen]);

  const handleConfirm = async () => {
    if (
      !binId ||
      !gridId ||
      !siteWorkstation ||
      !selectedCompartmentBinId ||
      !barcodeInput
    )
      return;
    try {
      const nextInventoryMovement = await setBinNotEmpty({
        productIdentifier: barcodeInput,
        compartment: selectedCompartment + 1,
        gridId,
        workstationId: siteWorkstation.id
      }).unwrap();
      setIsModalOpen(false);
      successToast(`Hold placed on UPC ${barcodeInput}`);
      dispatch(
        setSourceDestinationBins({
          sourceAndDestinationBin: {
            sourceBin: {
              ...nextInventoryMovement.sourceBin,
              autostoreBinNumber: nextInventoryMovement.sourceBin.autostoreBinId
            },
            destinationBin: {
              ...nextInventoryMovement.destinationBin,
              autostoreBinNumber:
                nextInventoryMovement.destinationBin.autostoreBinId
            }
          }
        })
      );
    } catch (error) {
      errorToast(getMessageFromRtkError(error), {
        trackingLocation: trackedModalName
      });
    }
  };
  const disabled = state !== "Bin Opened" || !isSourceBinReady;

  const compartmentOccupancy = getBinCompartmentOccupancy(binState);

  return (
    <>
      <Button
        variant="subtle"
        size="large"
        startIcon={<Warning20Px style={{ opacity: disabled ? 0.25 : 1 }} />}
        onClick={() => {
          setIsModalOpen(true);
          mixpanelTrack({
            type: "Button Click",
            label: "Bin Not Empty",
            trackedPageName
          });
        }}
        disabled={disabled}
      >
        {t("bin not empty")}
      </Button>
      <Dialog
        open={isModalOpen}
        onClose={(_event, reason) => {
          mixpanelTrackModalDismiss(trackedModalName, reason);
          setIsModalOpen(false);
        }}
        aria-labelledby="confirm-bin-not-empty-modal-title"
      >
        <DialogTitle id="confirm-bin-not-empty-modal-title">
          <Stack direction="row" gap={1}>
            <Warning20Px
              style={{ alignSelf: "center" }}
              fill={theme.palette.primary.dark}
            />
            {t("bin not empty")}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="column" gap={2} mt={2} alignItems="center">
            <Box
              height={isShortSideBin ? "300px" : "unset"}
              width={"100%"}
              justifyItems="center"
            >
              <AutostoreBin
                state={state}
                binId={binId}
                numberOfRows={numberOfRows}
                numberOfColumns={numberOfColumns}
                pickQuantity={""}
                pickCompartment={null}
                onBinClick={(compartment) => {
                  if (compartment === undefined) return;

                  const isCompartmentEmpty = isBinResponseCompartmentEmpty(
                    binState,
                    compartment
                  );

                  // do nothing if user clicks a full compartment
                  if (!isCompartmentEmpty) {
                    mixpanelTrack({
                      type: "Button Click",
                      label: "Full Compartment Selected",
                      trackedPageName: trackedModalName,
                      eventProperties: {
                        Compartment: compartment + 1,
                        "Bin Number": binId
                      }
                    });
                    return;
                  }

                  setSelectedCompartment(compartment);
                  mixpanelTrack({
                    type: "Button Click",
                    label: "Compartment Selected",
                    trackedPageName: trackedModalName,
                    eventProperties: {
                      Compartment: compartment + 1,
                      "Bin Number": binId
                    }
                  });
                }}
                selectedCompartment={selectedCompartment ?? undefined}
                compartmentOccupancy={compartmentOccupancy}
                isShortSideBin={isShortSideBin}
              />
            </Box>
            <Collapse in={!selectedCompartmentBinId}>
              <Typography variant="h5" mt={2}>
                {t("select compartment")}
              </Typography>
            </Collapse>
            <Collapse in={!!selectedCompartmentBinId}>
              <Typography variant="h5" mt={2}>
                {t("please scan the product in the bin")}
              </Typography>
              <TextField
                id="barcodeInput"
                disabled={!selectedCompartmentBinId}
                type="string"
                label="UPC"
                fullWidth
                value={barcodeInput}
                focused={!!barcodeInput}
                onChange={(event) => {
                  setBarcodeInput(event.target.value);
                }}
                onBlur={(event) => {
                  if (event.target.value !== "") {
                    mixpanelTrack({
                      type: "Input Change",
                      label: "Manual UPC Input",
                      eventProperties: {
                        UPC: event.target.value
                      },
                      trackedPageName: trackedModalName
                    });
                  }
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    mt: 0
                  },
                  mt: 2
                }}
              />
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ mb: 2 }}>
          <Stack justifyContent="center" direction="row" width="100%" gap={1}>
            <Button
              variant="subtle"
              size="large"
              startIcon={<CloseCircle20Px />}
              onClick={() => {
                setIsModalOpen(false);
                mixpanelTrack({
                  type: "Button Click",
                  label: "Cancel",
                  trackedPageName: trackedModalName
                });
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              size="large"
              startIcon={<CheckmarkCircle20Px fill="white" />}
              disabled={
                !barcodeInput ||
                !selectedCompartmentBinId ||
                isSetBinNotEmptyLoading
              }
              onClick={async () => {
                await handleConfirm();
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Button Click",
                  label: "Confirm",
                  eventProperties: {
                    UPC: barcodeInput,
                    Compartment: selectedCompartment
                      ? selectedCompartment + 1
                      : null,
                    "Bin Id": selectedCompartmentBinId
                  }
                });
              }}
            >
              {t("confirm")}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
